/**
 * VRG-691 — the Sales pipeline's vocabulary and arithmetic: stages, value basis, lead-source
 * groups, stage evidence, and the folds that turn one list of deals into the board, the KPI
 * tiles, the flow chart and the by-channel table.
 *
 * ── WHY ONE MODULE, SHARED BY THE API AND THE PLATFORM ─────────────────────────────────
 *
 * Spencer's requirement for the Pipeline tab is that the board, the tiles and the flow chart
 * RECONCILE: the deals in the Lead column, the "still open" drop-off, the open-pipeline tile.
 * The only way that holds by construction is for all of them to be folded from the same list
 * by the same functions. The API computes them (so a number on the page is the server's) and
 * the guard `sales-pipeline.guard.spec.ts` asserts the cross-totals agree.
 *
 * The potential-revenue model (VRG-545) also lives here now. It used to be a platform-only
 * module (`SignupsPage/potentialRevenue.ts`); a deal's ROI-basis value is the same figure,
 * and a second copy on the API would drift from the Signups grid on the first edit. The
 * platform module now delegates to `potentialRevenue` below.
 */
import {graduatedActivationValue, hasActivationTiers, activationTiersAgree, type ActivationTier} from '../billing/activation-tier.js';
import {PLAN_TIER_KEYS, type PlanTierKey} from '../billing/plan-tier.js';
import {clientTierLabel, DEFAULT_ACTIVATION_SHARE_BY_TIER, isClientTier, suggestTier, type ClientTier} from './client-tiers.js';
import type {PayingNowStatus} from './paying.js';
import {foldForecastAccuracy, type DealForecast, type ForecastAccuracyKpis} from './sales-forecast.js';
import {isSocialHost} from './social-network.js';
import {isSourceChannel, SOURCE_CHANNELS, type SourceChannel} from './source-channel.js';

// ── Stages ───────────────────────────────────────────────────────────────────────────────

/**
 * Spencer, 2026-10-13 (VRG-692, superseding VRG-691's side lane): Demo IS a stage, between
 * Access granted and Signed up. A deal may SKIP it (no demo is needed to reach Signed up) — as
 * it may skip Access granted — and a demo provisioned is its evidence when one exists. A
 * taken-over demo goes on to Signed up as before.
 *
 * `paid_client` is shown as "Won" (Spencer, same day). The KEY stays: it is stored in
 * `sales_deals.stage` and the history, and the definition (first paid invoice) did not change,
 * so renaming the key would be a data migration bought for nothing.
 *
 * VRG-727 (Spencer, 2026-10-20) — `account_created` is shown as "Signed up": the brand has a
 * platform account. Same reason the key stays. People and Companies are the two things on the
 * pipeline and both start as Lead. THIS MAP IS THE ONLY COPY of the stage names: the board, the
 * lists, the flow charts (Sales and Channels), the filters and the master pipeline all read it
 * (`sales-naming.guard.spec.ts` fails on a hardcoded copy in the platform's Sales surfaces).
 */
export const SALES_STAGES = ['lead', 'access_granted', 'demo', 'account_created', 'paid_client'] as const;
export type SalesStage = (typeof SALES_STAGES)[number];

export const SALES_STAGE_LABELS: Readonly<Record<SalesStage, string>> = {
  lead: 'Lead',
  access_granted: 'Access granted',
  demo: 'Demo',
  account_created: 'Signed up',
  paid_client: 'Won',
};

/** Stages a deal may pass over without anyone confirming: Vera simply never saw them. */
export const SKIPPABLE_STAGES: ReadonlySet<SalesStage> = new Set<SalesStage>(['access_granted', 'demo']);

export const isSalesStage = (v: unknown): v is SalesStage =>
  typeof v === 'string' && (SALES_STAGES as readonly string[]).includes(v);

export const stageIndex = (s: SalesStage): number => SALES_STAGES.indexOf(s);

/** How a value was set: by the system from evidence it holds, or by a person. */
export const SALES_SET_BY = ['auto', 'manual'] as const;
export type SalesSetBy = (typeof SALES_SET_BY)[number];

export const DEAL_STATUSES = ['open', 'closed'] as const;
export type DealStatus = (typeof DEAL_STATUSES)[number];

export const DEAL_CLOSED_REASONS = ['lost', 'archived'] as const;
export type DealClosedReason = (typeof DEAL_CLOSED_REASONS)[number];
export const DEAL_CLOSED_REASON_LABELS: Readonly<Record<DealClosedReason, string>> = {lost: 'Lost', archived: 'Archived'};
export const isDealClosedReason = (v: unknown): v is DealClosedReason =>
  typeof v === 'string' && (DEAL_CLOSED_REASONS as readonly string[]).includes(v);

/**
 * Board lanes: the five stages, then LOST — a column collapsed by default (Spencer, 2026-10-13).
 * Dropping a card on Lost closes the deal as lost; dragging it back out reopens it. Archived
 * deals are on no lane.
 */
export const SALES_BOARD_LANES = [...SALES_STAGES, 'lost'] as const;
export type SalesBoardLane = (typeof SALES_BOARD_LANES)[number];
export const SALES_BOARD_LANE_LABELS: Readonly<Record<SalesBoardLane, string>> = {
  ...SALES_STAGE_LABELS,
  lost: 'Lost',
};

// ── Stage probabilities (settings) ───────────────────────────────────────────────────────

export type StageProbabilities = Record<SalesStage, number>;

/**
 * Defaults from the brief; editable in the Pipeline tab's settings. Ratios, 0–1. Demo's 35% is
 * VRG-692's default (between Access granted and Signed up), not the brief's: Spencer to
 * confirm or change it in *Stage probabilities*.
 */
export const DEFAULT_STAGE_PROBABILITIES: Readonly<StageProbabilities> = {
  lead: 0.1,
  access_granted: 0.25,
  demo: 0.35,
  account_created: 0.5,
  paid_client: 1,
};

/**
 * A full, valid probability map or null. Each stage a finite ratio in [0, 1]. A map saved
 * before Demo was a stage (no `demo` key) is still valid: Demo takes its default.
 */
export const parseStageProbabilities = (v: unknown): StageProbabilities | null => {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const out = {} as StageProbabilities;
  for (const s of SALES_STAGES) {
    const raw = (v as Record<string, unknown>)[s];
    const n = raw === undefined && s === 'demo' ? DEFAULT_STAGE_PROBABILITIES.demo : raw;
    if (typeof n !== 'number' || !Number.isFinite(n) || n < 0 || n > 1) return null;
    out[s] = n;
  }
  return out;
};

// ── Lead-source groups ───────────────────────────────────────────────────────────────────

/**
 * The coarse "where did this come from", named EXACTLY as the channels (Spencer, 2026-10-13,
 * VRG-692; VRG-691's first cut was Website, Social media, Blog / Insights, Trade show, Outreach,
 * Referral, Paid, Other). A DISPLAY grouping of the 19 source channels (the stored field), so it
 * never disagrees with them; the sub-source label carries the finer origin.
 *
 * NOT persisted anywhere: the group is derived from the channel on every read. The only place a
 * group KEY survives a page load is the Pipeline tab's `?source=` URL parameter (a bookmark, a
 * pasted link), which is why the renamed keys keep an alias below.
 */
export const LEAD_SOURCE_GROUPS = [
  'website',
  'social',
  'content',
  'events',
  'sales',
  'referral',
  'pr',
  'partners',
  'email',
  'sem_aem',
  'offline_ads',
  // VRG-701 — a later deal at a company that is already a client. NOT a channel: no channel maps
  // here (CHANNEL_LEAD_SOURCE_GROUP never targets it), like Not recorded. See `DEAL_SOURCES`.
  'expansion',
  'other',
  'not_recorded',
] as const;
export type LeadSourceGroup = (typeof LEAD_SOURCE_GROUPS)[number];

export const LEAD_SOURCE_GROUP_LABELS: Readonly<Record<LeadSourceGroup, string>> = {
  website: 'Website',
  social: 'Social media',
  content: 'Content',
  events: 'Events',
  sales: 'Sales',
  referral: 'Referral',
  pr: 'PR / press',
  partners: 'Partners',
  email: 'Email',
  // Spencer's wording. It covers SEM (#4) only; "AEM" names answer-engine placements, which
  // vera-2 does not buy today. Offline ads (#6), which sat in the old "Paid" group, is NOT
  // search marketing, so it has its own group rather than hiding under this label.
  sem_aem: 'SEM / AEM',
  offline_ads: 'Offline ads',
  expansion: 'Expansion',
  other: 'Other',
  not_recorded: 'Not recorded',
};

/**
 * VRG-691's group keys that VRG-692 renamed. `content` kept its key (only the label changed).
 * `paid` held SEM and Offline ads; it resolves to SEM / AEM, the larger and only operated half.
 */
export const LEGACY_LEAD_SOURCE_GROUP_ALIASES: Readonly<Record<string, LeadSourceGroup>> = {
  trade_show: 'events',
  outreach: 'sales',
  paid: 'sem_aem',
};

export const isLeadSourceGroup = (v: unknown): v is LeadSourceGroup =>
  typeof v === 'string' && (LEAD_SOURCE_GROUPS as readonly string[]).includes(v);

/** A group key from outside (a URL), with VRG-691's keys still understood. Null if neither. */
export const parseLeadSourceGroup = (v: unknown): LeadSourceGroup | null => {
  if (isLeadSourceGroup(v)) return v;
  if (typeof v === 'string' && Object.prototype.hasOwnProperty.call(LEGACY_LEAD_SOURCE_GROUP_ALIASES, v)) {
    return LEGACY_LEAD_SOURCE_GROUP_ALIASES[v];
  }
  return null;
};

/**
 * Channel → group. TOTAL over SourceChannel, so a new channel is a compile error here until it
 * is placed in a group (working-conventions §77). Rows by Traction number:
 *   #1 targeting blogs, #8 content marketing → Content
 *   #2 publicity, #3 unconventional PR       → PR / press
 *   #4 SEM                                   → SEM / AEM
 *   #5 social and display ads (organic only per the canon), #19 community → Social media
 *   #6 offline ads                           → Offline ads
 *   #7 SEO, #11 engineering as marketing     → Website (both land on vera.inc)
 *   #9 email marketing                       → Email
 *   #10 viral, #14 affiliate programs        → Referral
 *   #12 Partnerships (stored as `business_development`), #15 existing platforms → Partners
 *   #13 Sales (stored as `outreach`)         → Sales
 *   #16 trade shows, #17 offline events, #18 speaking → Events
 * Nothing maps to Other today; it stays for a channel added before anyone decides its group
 * deliberately (the guard `gtm-channel-coverage.guard.spec.ts` says when that has happened).
 */
export const CHANNEL_LEAD_SOURCE_GROUP: Readonly<Record<SourceChannel, LeadSourceGroup>> = {
  targeting_blogs: 'content',
  publicity: 'pr',
  unconventional_pr: 'pr',
  sem: 'sem_aem',
  social_display_ads: 'social',
  offline_ads: 'offline_ads',
  seo: 'website',
  content_marketing: 'content',
  email_marketing: 'email',
  viral_marketing: 'referral',
  engineering_as_marketing: 'website',
  business_development: 'partners',
  outreach: 'sales',
  affiliate_programs: 'referral',
  existing_platforms: 'partners',
  trade_shows: 'events',
  offline_events: 'events',
  speaking: 'events',
  community_building: 'social',
};

/**
 * The group a deal's source falls in. No channel: a website signup is still "Website"
 * (referrer unknown) — it demonstrably came through vera.inc — while anything else is
 * "Not recorded", never guessed.
 */
export const leadSourceGroup = (channel: SourceChannel | null | undefined, websiteOrigin: boolean): LeadSourceGroup =>
  channel && isSourceChannel(channel) ? CHANNEL_LEAD_SOURCE_GROUP[channel] : websiteOrigin ? 'website' : 'not_recorded';

// ── A deal's own source (VRG-701) ────────────────────────────────────────────────────────

/**
 * VRG-701 — Spencer, 2026-10-14: a company can have several deals (an upsell, a pilot → rollout).
 * A deal's SOURCE is then a property of the deal, not only of the company:
 *
 *   - stored NULL  → the deal INHERITS the company's first-touch source (`resolveLeadSource`).
 *                    Every deal that existed before VRG-701, and every company's first deal;
 *   - a channel    → set by hand on the deal;
 *   - `expansion`  → the company was already a client. A new deal at a company with a Won deal
 *                    defaults to it (`defaultDealSource`), editable.
 *
 * Expansion is NOT a channel (no Traction number, no overview row, no CHANNEL_LEAD_SOURCE_GROUP
 * entry) — it is a non-channel source like Not recorded, so the master pipeline shows it as its
 * own source and an upsell never double-credits the channel that brought the company in. The
 * company-lifetime view (`MasterAttribution` 'first_touch') puts it back on that channel.
 *
 * Stored as text + CHECK on `sales_deals.source` (migration 0155): the 19 channels + expansion.
 */
export const EXPANSION_SOURCE = 'expansion' as const;
export const DEAL_SOURCES = [...SOURCE_CHANNELS, EXPANSION_SOURCE] as const;
export type DealSource = (typeof DEAL_SOURCES)[number];
export const isDealSource = (v: unknown): v is DealSource => typeof v === 'string' && (DEAL_SOURCES as readonly string[]).includes(v);

/** Lead-source groups no channel maps to: shown as sources of their own, never redistributed. */
export const NON_CHANNEL_LEAD_SOURCE_GROUPS: readonly LeadSourceGroup[] = ['expansion', 'not_recorded'];

/** The source a NEW deal starts with: Expansion when the company already has a Won deal, else inherit (null). */
export const defaultDealSource = (companyHasWonDeal: boolean): DealSource | null => (companyHasWonDeal ? EXPANSION_SOURCE : null);

/** A deal's stored source → its channel and group. Null source = inherit (the caller passes the company's). */
export const dealSourceGroup = (source: DealSource): {channel: SourceChannel | null; group: LeadSourceGroup} =>
  source === EXPANSION_SOURCE ? {channel: null, group: 'expansion'} : {channel: source, group: CHANNEL_LEAD_SOURCE_GROUP[source]};

// ── A deal's plan (VRG-701) ──────────────────────────────────────────────────────────────

/**
 * VRG-701 (Spencer, 2026-10-14) — the subscription PLAN a deal is on or is buying, single-select,
 * keyed by the plan catalog's stable identity (`PLAN_TIER_KEYS`, VRG-365: never by its Stripe
 * title). It replaces 0152's company-level "Vera product" (the tag set), which was the wrong
 * dimension: activation pricing does not differ by tag type (one graduated metered price, shared
 * by every plan, `billing/activation-tier.ts`), so a tag type moved no figure.
 *
 * When the deal's company has a PAYING subscription, the plan comes from Stripe (its
 * `stripe_subscription_products.tier_key`) and is shown "from Stripe", read-only; otherwise it is
 * the deal's own `sales_deals.plan` (CHECK, migration 0155). These labels are display copy only.
 */
export const DEAL_PLANS = PLAN_TIER_KEYS;
export type DealPlan = PlanTierKey;
export const DEAL_PLAN_LABELS: Readonly<Record<DealPlan, string>> = {
  starter: 'Starter',
  individual: 'Individual',
  business: 'Business',
  scale: 'Scale',
  enterprise: 'Enterprise',
};
export const isDealPlan = (v: unknown): v is DealPlan => typeof v === 'string' && (PLAN_TIER_KEYS as readonly string[]).includes(v);
export type DealPlanSource = 'stripe' | 'deal';

/** Where the sub-source came from, when vera-2 filled it in. `manual` is a person. */
export const LEAD_SOURCE_REF_KINDS = [
  'company_channel',
  'person_channel',
  'outreach_campaign',
  'referral',
  'tracked_link',
  'website_journey',
  'website_unknown',
  'trade_show',
  'utm',
  // VRG-728 — a website lead's own first recorded touch (referrer / landing page, no UTMs),
  // captured by the site for EVERY visitor and stored on the signup. Distinct from
  // `website_journey`, which is PostHog's analytics-on sample.
  'website_first_touch',
] as const;
export type LeadSourceRefKind = (typeof LEAD_SOURCE_REF_KINDS)[number];

/**
 * First-touch referrer host → channel, for website leads whose visit was recorded with
 * analytics on (`website_lead_journeys`). Deliberately short: a host not listed here keeps the
 * channel unset and the host as the sub-source label, rather than a guessed bucket.
 */
export const referrerChannel = (host: string | null | undefined, landingPath?: string | null): SourceChannel | null => {
  const h = (host ?? '').toLowerCase().replace(/^www\./, '');
  if (h) {
    if (/(^|\.)(google|bing|duckduckgo|yahoo|ecosia|baidu|yandex|brave|startpage)\./.test(h)) return 'seo';
    if (/(^|\.)(chatgpt\.com|openai\.com|perplexity\.ai|claude\.ai|gemini\.google\.com|copilot\.microsoft\.com)$/.test(h)) return 'seo';
    // VRG-735 — one host list for "is it Social" and "which network" (gtm/social-network.ts).
    if (isSocialHost(h)) return 'social_display_ads';
  }
  const p = (landingPath ?? '').toLowerCase();
  if (p.startsWith('/insights') || p.startsWith('/learn')) return 'content_marketing';
  return null;
};

/**
 * VRG-728 — a link's UTM tags → channel, per the naming convention in vera-inc
 * `gtm/attribution/utm-convention.md` (⚠️ that table and this function are ONE contract; change
 * them together). `utm_medium` carries the channel; `utm_source` only breaks the ties the
 * convention names. Deliberately short, like `referrerChannel`: a medium not listed keeps the
 * channel unset rather than guessing a bucket, and the UTM values still show as the sub-source
 * label, so an off-convention link is visible rather than silently filed somewhere.
 *
 * `link` (vera.inc/invite tracked links) maps to nothing on purpose: the tracked-link signal
 * already names those, and its campaign is a person's link label, not a channel.
 */
export const utmChannel = (source: string | null | undefined, medium: string | null | undefined): SourceChannel | null => {
  const m = (medium ?? '').trim().toLowerCase().replace(/[\s_]+/g, '-');
  const s = (source ?? '').trim().toLowerCase();
  switch (m) {
    case 'cpc':
    case 'ppc':
    case 'paid-search':
      return 'sem';
    case 'paid-social':
    case 'social':
      // #5 is organic-only per the canon; paid social is mapped here too so a stray paid link is
      // still filed under its channel rather than lost — the medium shows it was paid.
      return 'social_display_ads';
    case 'outreach':
    case 'field':
      // #13 Sales (stored as `outreach`): one-to-one emails, LinkedIn messages, and the
      // field-visit leave-behinds.
      return 'outreach';
    case 'email':
      return s === 'outreach' || s === 'apollo' ? 'outreach' : 'email_marketing';
    case 'affiliate':
      return 'affiliate_programs';
    case 'pr':
      return 'publicity';
    case 'event':
      return 'offline_events';
    case 'trade-show':
      return 'trade_shows';
    case 'partner':
      return 'business_development';
    case 'organic':
      return 'seo';
    default:
      return null;
  }
};

// ── Anticipated value ────────────────────────────────────────────────────────────────────

/**
 * VRG-701 adds `deal`: the DEAL's own calculator inputs, edited on the deal (Spencer,
 * 2026-10-14). Precedence: manual > deal > roi (the company's calculator) > form.
 */
export const DEAL_VALUE_BASES = ['manual', 'deal', 'roi', 'form'] as const;
export type DealValueBasis = (typeof DEAL_VALUE_BASES)[number];
/**
 * `roi` is the ROI-CALCULATOR basis (its units priced on the activation ladder). Labelled
 * "Calculator", not "ROI" (VRG-692): a chip reading "ROI" on a revenue figure implied a "Vera ROI"
 * metric, which does not exist. The key stays `roi` (it is in snapshots and the API).
 */
export const DEAL_VALUE_BASIS_LABELS: Readonly<Record<DealValueBasis, string>> = {manual: 'Manual', deal: 'Deal inputs', roi: 'Calculator', form: 'Form'};
export const isDealValueBasis = (v: unknown): v is DealValueBasis => typeof v === 'string' && (DEAL_VALUE_BASES as readonly string[]).includes(v);

/** The currency every derived value is in: the plan catalog's (Stripe amounts are USD cents). */
export const DEAL_VALUE_CURRENCY = 'USD';

/** The plan-catalog fields the value model reads (a `stripe_subscription_products` row). */
export interface PricingPlanLike {
  tier_key?: string | null;
  interval?: string | null;
  /** Stripe `unit_amount`-shaped: CENTS. */
  amount?: number | null;
  active?: boolean | null;
  activation_tiers?: ActivationTier[] | null;
}

/**
 * vera-website seeds the ROI calculator's units slider at 25,000, and `assumptionsTouched`
 * never covers `units`, so a submitted 25,000 is indistinguishable from an untouched slider.
 * It is refused as the visitor's figure. (Mirrors `websiteFields.ts`'s `units` default.)
 */
export const ROI_UNITS_SEEDED_DEFAULT = 25_000;

/**
 * Enterprise is the contact-sales tier with no price; it is priced as Scale, the closest thing
 * with one (founder's decision 2026-09-07, `potentialRevenue.ts`). A FLOOR, flagged on the row.
 */
export const UNPRICED_TIER_SUBSTITUTES: Readonly<Record<string, string>> = {enterprise: 'scale'};

const isMonthlyInterval = (interval: string | null | undefined): boolean => {
  const v = (interval || '').toString().toLowerCase();
  return v === 'month' || v === 'monthly';
};

/** The active monthly plan row for a tier key, or null (annual rows would apply the discount ×12). */
export const monthlyPlanForTier = <P extends PricingPlanLike>(plans: readonly P[], tierKey: string): P | null =>
  plans.find(p => p.active !== false && isMonthlyInterval(p.interval) && (p.tier_key ?? '').toLowerCase() === tierKey) ?? null;

/** The tier key a lead asked for, normalised, and the one it is priced as. */
export const planInterestTier = (planInterest: unknown): {requested: string | null; priced: string | null} => {
  const requested = typeof planInterest === 'string' ? planInterest.trim().toLowerCase() || null : null;
  return {requested, priced: requested ? (UNPRICED_TIER_SUBSTITUTES[requested] ?? requested) : null};
};

export interface PotentialRevenue<P extends PricingPlanLike = PricingPlanLike> {
  /** Annualised graduated activation spend, USD. */
  activationAnnual: number;
  /** VRG-736 — the share of units expected to activate (1 unless the caller passed one). */
  activationShare: number;
  /** VRG-736 — units × share: the activations a year that are priced. */
  expectedActivations: number;
  /** Annualised price of the plan the visitor said they wanted, USD (0 with no plan). */
  planAnnual: number;
  total: number;
  monthlyActivations: number;
  plan: P | null;
  planSubstituted: boolean;
  requestedTierKey: string | null;
}

/**
 * VRG-545's potential-revenue model, now shared. `null` whenever an input is ours rather than
 * the visitor's or is missing:
 *   - units absent, non-finite or ≤ 0;
 *   - `unitsSupplied` false (the caller's verdict that units is the visitor's, not the seed);
 *   - no usable activation schedule (a wrong price is worse than no price).
 *
 *     expected activations = units × activation share          (VRG-736; share 1 when not given)
 *     monthly activations  = expected activations ÷ 12
 *     activation cost      = graduated per-activation rate on that monthly volume, × 12
 *     plan cost            = monthly price of `planInterest` (enterprise → scale), × 12
 *     potential revenue    = activation cost + plan cost
 *
 * THE ONE FORMULA: the Signups grid (no share: every unit), a deal's calculator basis and a
 * deal's own inputs (`dealPotentialRevenue`, with the deal's share) all call this. Tags are NOT
 * in it: tag margin is hardware and is a separate line (`tagMargin`), never revenue here.
 */
export const potentialRevenue = <P extends PricingPlanLike>(
  input: {units: unknown; unitsSupplied: boolean; planInterest: unknown; activationShare?: number | null},
  plans: readonly P[],
): PotentialRevenue<P> | null => {
  const units = typeof input.units === 'number' ? input.units : Number(input.units);
  if (input.units === null || input.units === undefined || !Number.isFinite(units) || units <= 0) return null;
  if (!input.unitsSupplied) return null;
  const share = parseActivationShare(input.activationShare) ?? 1;

  const {requested, priced} = planInterestTier(input.planInterest);
  const plan = priced ? monthlyPlanForTier(plans, priced) : null;

  // The visitor's plan carries its own ladder when it has one; with no plan the catalog's
  // schedules must AGREE for "the" schedule to exist, else nothing is priced.
  let tiers: ActivationTier[] | null = null;
  if (plan && hasActivationTiers(plan.activation_tiers ?? null)) tiers = plan.activation_tiers ?? null;
  else {
    const schedules = plans.map(p => p.activation_tiers ?? null);
    if (activationTiersAgree(schedules)) tiers = schedules.find(s => hasActivationTiers(s)) ?? null;
  }

  const expectedActivations = units * share;
  const monthlyActivations = expectedActivations / 12;
  const monthlyCost = graduatedActivationValue(monthlyActivations, tiers);
  if (monthlyCost === null) return null;
  const activationAnnual = monthlyCost * 12;
  const planAnnual = plan ? ((plan.amount ?? 0) / 100) * 12 : 0;
  return {
    activationAnnual,
    activationShare: share,
    expectedActivations,
    planAnnual,
    total: activationAnnual + planAnnual,
    monthlyActivations,
    plan,
    planSubstituted: !!plan && !!requested && priced !== requested,
    requestedTierKey: requested,
  };
};

/**
 * The FORM basis: the waitlist form carries a plan tier but no volume, so its value is the
 * plan's annual price alone — `monthly price × 12`, enterprise priced as Scale. Null with no
 * (priced) plan of interest.
 */
export const formPlanValue = <P extends PricingPlanLike>(
  planInterest: unknown,
  plans: readonly P[],
): {annual: number; tierKey: string; substituted: boolean} | null => {
  const {requested, priced} = planInterestTier(planInterest);
  if (!priced) return null;
  const plan = monthlyPlanForTier(plans, priced);
  if (!plan || !(plan.amount && plan.amount > 0)) return null;
  return {annual: (plan.amount / 100) * 12, tierKey: requested ?? priced, substituted: priced !== requested};
};

export interface DealValue {
  /** Annual figure in `currency`, or null when no basis yields one. */
  amount: number | null;
  currency: string;
  basis: DealValueBasis | null;
  /** One line of provenance for the card's tooltip. */
  detail: string | null;
}

export interface DealValueCandidates {
  manual?: {amount: number; currency: string; detail?: string | null} | null;
  /** VRG-701 — the deal's own (edited) calculator inputs, priced by `dealPotentialRevenue`. */
  deal?: {amount: number; detail?: string | null} | null;
  roi?: {amount: number; detail?: string | null} | null;
  form?: {amount: number; detail?: string | null} | null;
}

/** Precedence: manual > deal > roi > form (VRG-701). A manual figure ALWAYS wins, even a smaller one. */
export const resolveDealValue = (c: DealValueCandidates): DealValue => {
  if (c.manual && Number.isFinite(c.manual.amount)) {
    return {amount: c.manual.amount, currency: c.manual.currency, basis: 'manual', detail: c.manual.detail ?? null};
  }
  if (c.deal && Number.isFinite(c.deal.amount)) {
    return {amount: c.deal.amount, currency: DEAL_VALUE_CURRENCY, basis: 'deal', detail: c.deal.detail ?? null};
  }
  if (c.roi && Number.isFinite(c.roi.amount)) {
    return {amount: c.roi.amount, currency: DEAL_VALUE_CURRENCY, basis: 'roi', detail: c.roi.detail ?? null};
  }
  if (c.form && Number.isFinite(c.form.amount)) {
    return {amount: c.form.amount, currency: DEAL_VALUE_CURRENCY, basis: 'form', detail: c.form.detail ?? null};
  }
  return {amount: null, currency: DEAL_VALUE_CURRENCY, basis: null, detail: null};
};

// ── Activation share and tag margin (VRG-736) ────────────────────────────────────────────

/** A ratio in [0, 1], or null. */
export const parseActivationShare = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) && n >= 0 && n <= 1 ? n : null;
};

/**
 * Where a deal's activation share came from: set on the DEAL, its company's TIER default, the
 * default of the tier its production SUGGESTS (no tier set), or NONE (no tier and no production:
 * every unit, which prices nothing since there are no units).
 */
export const ACTIVATION_SHARE_SOURCES = ['deal', 'tier', 'suggested_tier', 'none'] as const;
export type ActivationShareSource = (typeof ACTIVATION_SHARE_SOURCES)[number];

export interface ActivationShare {
  /** Ratio 0–1. */
  share: number;
  source: ActivationShareSource;
  /** The tier whose default applied (tier / suggested_tier), else null. */
  tier: ClientTier | null;
}

/**
 * VRG-736 (Spencer, 2026-10-22) — the share of a deal's annual production expected to activate:
 * the deal's own figure, else its tier's default (`DEFAULT_ACTIVATION_SHARE_BY_TIER`: Tier 1–2
 * 20%, Tier 3–5 100%), else the default of the tier its production suggests.
 */
export const resolveActivationShare = (input: {deal?: unknown; tier?: unknown; units?: number | null}): ActivationShare => {
  const own = parseActivationShare(input.deal);
  if (own !== null) return {share: own, source: 'deal', tier: isClientTier(input.tier) ? input.tier : null};
  if (isClientTier(input.tier)) return {share: DEFAULT_ACTIVATION_SHARE_BY_TIER[input.tier], source: 'tier', tier: input.tier};
  const suggested = suggestTier(input.units ?? null);
  if (suggested) return {share: DEFAULT_ACTIVATION_SHARE_BY_TIER[suggested], source: 'suggested_tier', tier: suggested};
  return {share: 1, source: 'none', tier: null};
};

/** "20%", "12.5%". */
export const percentLabel = (ratio: number): string => `${Math.round(ratio * 1000) / 10}%`;

/** Why the share is what it is, in words: "Tier 2 default", "set on the deal", "Tier 3 default, suggested from production". */
export const activationShareWhy = (s: ActivationShare): string =>
  s.source === 'deal'
    ? 'set on the deal'
    : s.source === 'tier' && s.tier
      ? `${clientTierLabel(s.tier)} default`
      : s.source === 'suggested_tier' && s.tier
        ? `${clientTierLabel(s.tier)} default, tier suggested from production`
        : 'no tier: every unit';

/**
 * The two Sales settings tag margin reads (`sales_pipeline_settings.tag_unit_cost_usd`,
 * `tag_markup`). NULL = not set: the PLACEHOLDER below applies, and says so.
 */
export interface TagEconomicsSettings {
  unit_cost_usd: number | null;
  /** Ratio, e.g. 0.35 for 35%. */
  markup: number | null;
}

/**
 * ⚠️ PLACEHOLDERS (VRG-736), until the supply price list lands (VRG-143). Not read from the
 * vera-inc tag catalog on purpose: its `CHIP_COST` / `FORM_CONV` figures are known to be wrong.
 * $1.00 a tag is a round number nobody will mistake for a quote; 35% is the markup Spencer used
 * in the brief's example. Both are editable in Sales → Tag economics.
 */
export const DEFAULT_TAG_ECONOMICS: Readonly<{unit_cost_usd: number; markup: number}> = {unit_cost_usd: 1, markup: 0.35};
export const TAG_COST_PLACEHOLDER_NOTE = 'placeholder until the supply price list (VRG-143)';

export interface TagEconomics {
  unit_cost_usd: number;
  markup: number;
  /** `placeholder` = the coded default (nothing saved); `setting` = saved in Sales settings. */
  unit_cost_source: 'placeholder' | 'setting';
  markup_source: 'placeholder' | 'setting';
}

export const resolveTagEconomics = (s: Partial<TagEconomicsSettings> | null | undefined): TagEconomics => {
  const cost = typeof s?.unit_cost_usd === 'number' && Number.isFinite(s.unit_cost_usd) && s.unit_cost_usd >= 0 ? s.unit_cost_usd : null;
  const markup = typeof s?.markup === 'number' && Number.isFinite(s.markup) && s.markup >= 0 ? s.markup : null;
  return {
    unit_cost_usd: cost ?? DEFAULT_TAG_ECONOMICS.unit_cost_usd,
    markup: markup ?? DEFAULT_TAG_ECONOMICS.markup,
    unit_cost_source: cost === null ? 'placeholder' : 'setting',
    markup_source: markup === null ? 'placeholder' : 'setting',
  };
};

/** The bounds the API enforces on a saved setting: cost $0–$1,000 a tag, markup 0–1,000%. */
export const TAG_UNIT_COST_MAX_USD = 1000;
export const TAG_MARKUP_MAX = 10;

/**
 * ── THE SEAM (TODO(VRG-143)) ── the cost of one tag. Today: the one Sales setting (or its
 * placeholder) for every tag. When the supply price list lands, read it here per FORM FACTOR
 * (sticker, dial, clothing label…) and fall back to the setting; nothing else changes, because
 * `tagMargin` is the only caller. The cost is "placeholder" until then whatever the setting says,
 * since a hand-typed figure is not a price list either.
 */
export const tagUnitCostUsd = (econ: TagEconomics, _formFactor?: string | null): {usd: number; placeholder: boolean} => ({
  usd: econ.unit_cost_usd,
  placeholder: true,
});

export interface TagMargin {
  /** USD a year, whole dollars. A SEPARATE line: never part of predicted ARR. */
  annual: number;
  /** Tagged units a year = expected activations (each activation needs a tag). */
  tagged_units: number;
  unit_cost_usd: number;
  markup: number;
  unit_cost_placeholder: boolean;
  markup_source: 'placeholder' | 'setting';
}

/**
 * VRG-736 — tag margin a year = expected tagged units × tag unit cost × markup. Tags are hardware:
 * this is margin on goods sold, so it is NOT recurring revenue and is never added into ARR (that
 * would overstate it); it sits beside ARR and is summed with it only as "predicted annual value".
 * Null with no expected activations (no production figure).
 */
export const tagMargin = (expectedActivations: number | null | undefined, econ: TagEconomics, formFactor?: string | null): TagMargin | null => {
  if (typeof expectedActivations !== 'number' || !Number.isFinite(expectedActivations) || expectedActivations < 0) return null;
  const cost = tagUnitCostUsd(econ, formFactor);
  return {
    annual: Math.round(expectedActivations * cost.usd * econ.markup),
    tagged_units: expectedActivations,
    unit_cost_usd: cost.usd,
    markup: econ.markup,
    unit_cost_placeholder: cost.placeholder,
    markup_source: econ.markup_source,
  };
};

/** Predicted annual value = predicted ARR + tag margin, USD. Null without a USD ARR. */
export const predictedAnnualValue = (arr: {amount: number | null; currency: string}, tag: TagMargin | null | undefined): number | null =>
  arr.amount !== null && Number.isFinite(arr.amount) && arr.currency === DEAL_VALUE_CURRENCY ? arr.amount + (tag?.annual ?? 0) : null;

// ── A deal's potential revenue (VRG-701) ─────────────────────────────────────────────────

/** Where the plan a deal is priced on came from, strongest first. */
export const POTENTIAL_PLAN_SOURCES = ['stripe', 'deal', 'form', 'tier'] as const;
export type PotentialPlanSource = (typeof POTENTIAL_PLAN_SOURCES)[number];

/** The ROI-calculator inputs a deal carries: its own when edited, else its company's newest. */
export interface DealCalcInputs {
  /** Annual products / certificates (the calculator's "Units per year"). */
  units: number | null;
  /** Average retail price, and customer acquisition cost (the calculator's), USD. */
  price: number | null;
  cac: number | null;
  /** `calculator` = the company's submission; `edited` = set on the deal; null = neither. */
  source: 'calculator' | 'edited' | null;
}

export interface DealPotentialRevenue {
  inputs: DealCalcInputs;
  /** The plan priced, and where it came from: Stripe > the deal > the waitlist form > the tier. */
  plan: string | null;
  plan_source: PotentialPlanSource | null;
  /** Enterprise has no price: priced as Scale, a floor (`UNPRICED_TIER_SUBSTITUTES`). */
  plan_substituted: boolean;
  /** Annual, USD: the plan's monthly list price × 12. Null with no (priced) plan. */
  subscription_annual: number | null;
  /** Annual, USD: annual units on the graduated activation ladder. Null with no units or schedule. */
  activation_annual: number | null;
  /** subscription + activation, either missing counted as 0; null when both are missing. ARR only: no tags. */
  total: number | null;
  /** VRG-736 — the share of production expected to activate, and where it came from. */
  activation_share: ActivationShare;
  /** VRG-736 — units × share, a year; null with no units. */
  expected_activations: number | null;
  /** VRG-736 — the plan's monthly list price, USD (for the formula); null with no priced plan. */
  plan_monthly: number | null;
  /** VRG-736 — tag margin a year, a separate line; null with no units or no tag economics given. */
  tag_margin: TagMargin | null;
}

/**
 * VRG-736 — what a deal's predicted ARR was computed from, so its tooltip can show the formula
 * with the deal's own numbers (`predictedArrFormula`). Built by the API for whichever basis won;
 * `calculated` is the calculator's figure even when a manual one overrides it.
 */
export interface PredictedArrBreakdown {
  basis: DealValueBasis | null;
  /** The predicted ARR shown (= `value.amount`). */
  arr: number | null;
  currency: string;
  plan: string | null;
  plan_source: PotentialPlanSource | null;
  plan_substituted: boolean;
  plan_monthly: number | null;
  plan_annual: number | null;
  units: number | null;
  share: ActivationShare;
  expected_activations: number | null;
  activation_annual: number | null;
  /** plan + activations as calculated (null with neither): what a manual figure overrides. */
  calculated: number | null;
}

/**
 * VRG-701 — a deal's POTENTIAL revenue (Spencer, 2026-10-14): the subscription for its plan, plus
 * annual products × the activation price. NOT a second formula: it is VRG-545's `potentialRevenue`
 * (the Signups grid's model and the VRG-692 calculator basis), priced on the plan catalog the API
 * reads from Stripe (`stripe_subscription_products`: each plan's monthly `amount` and its
 * `activation_tiers` schedule). Nothing here hard-codes a price.
 */
export const dealPotentialRevenue = <P extends PricingPlanLike>(
  inputs: DealCalcInputs,
  plan: {key: string | null; source: PotentialPlanSource | null},
  plans: readonly P[],
  /**
   * VRG-736 — the deal's activation share (`resolveActivationShare`; absent = every unit, as before)
   * and the tag economics (absent = no tag margin). Neither changes the formula: the share scales
   * the units `potentialRevenue` prices, and tag margin is computed beside it, never inside it.
   */
  opts: {share?: ActivationShare; tags?: TagEconomics} = {},
): DealPotentialRevenue => {
  const units = typeof inputs.units === 'number' && Number.isFinite(inputs.units) && inputs.units > 0 ? inputs.units : null;
  const share: ActivationShare = opts.share ?? {share: 1, source: 'none', tier: null};
  const pr = units !== null ? potentialRevenue({units, unitsSupplied: true, planInterest: plan.key, activationShare: share.share}, plans) : null;
  const sub = plan.key ? formPlanValue(plan.key, plans) : null;
  const subscription = pr && pr.plan ? Math.round(pr.planAnnual) : sub ? Math.round(sub.annual) : null;
  const activation = pr ? Math.round(pr.activationAnnual) : null;
  const expected = units !== null ? units * share.share : null;
  return {
    inputs,
    plan: plan.key,
    plan_source: plan.key ? plan.source : null,
    plan_substituted: pr ? pr.planSubstituted : !!sub?.substituted,
    subscription_annual: subscription,
    activation_annual: activation,
    total: subscription === null && activation === null ? null : (subscription ?? 0) + (activation ?? 0),
    activation_share: share,
    expected_activations: expected,
    plan_monthly: pr && pr.plan ? (pr.plan.amount ?? 0) / 100 : sub ? sub.annual / 12 : null,
    tag_margin: opts.tags ? tagMargin(expected, opts.tags) : null,
  };
};

// ── Stage evidence ───────────────────────────────────────────────────────────────────────

/** When the system saw each automatic stage's evidence (ISO), or null when it has none. */
export interface StageEvidence {
  access_granted: string | null;
  /** A demo was provisioned for the company (VRG-692). Optional so older callers read "none". */
  demo?: string | null;
  account_created: string | null;
  paid_client: string | null;
}

/** The furthest stage the evidence supports. Lead needs none. */
export const evidenceStage = (e: StageEvidence): SalesStage =>
  e.paid_client
    ? 'paid_client'
    : e.account_created
      ? 'account_created'
      : e.demo
        ? 'demo'
        : e.access_granted
          ? 'access_granted'
          : 'lead';

export type StageMoveCheck =
  | {kind: 'ok'}
  /** Past the evidence: allowed only when a person confirms; recorded as manual. */
  | {kind: 'confirm'; missing: SalesStage[]}
  | {kind: 'refused'; reason: string};

/**
 * Can a person move a deal from `from` to `to`?
 *   - backward BELOW the evidence → refused: the evidence would put it straight back;
 *   - forward PAST the evidence → needs confirmation, names the stages the system cannot see;
 *   - anything else → ok.
 */
export const checkStageMove = (from: SalesStage, to: SalesStage, e: StageEvidence): StageMoveCheck => {
  if (from === to) return {kind: 'refused', reason: 'The deal is already at this stage.'};
  const ev = evidenceStage(e);
  if (stageIndex(to) < stageIndex(ev)) {
    return {
      kind: 'refused',
      reason: `Vera has evidence for ${SALES_STAGE_LABELS[ev]}, so the deal cannot sit before it. Unlink the company to undo it.`,
    };
  }
  if (stageIndex(to) > stageIndex(ev)) {
    // Demo is skippable: moving past it does not claim a demo happened, so it is not "missing"
    // unless Demo itself is the target.
    const missing = SALES_STAGES.filter(
      s => stageIndex(s) > stageIndex(ev) && stageIndex(s) <= stageIndex(to) && (s !== 'demo' || to === 'demo'),
    );
    return {kind: 'confirm', missing};
  }
  return {kind: 'ok'};
};

/** The automatic stage moves evidence implies for a deal at `current`: forward only, in order. */
export const autoAdvanceSteps = (current: SalesStage, e: StageEvidence): Array<{stage: SalesStage; at: string}> => {
  const out: Array<{stage: SalesStage; at: string}> = [];
  const target = evidenceStage(e);
  if (stageIndex(target) <= stageIndex(current)) return out;
  for (const s of SALES_STAGES) {
    if (stageIndex(s) <= stageIndex(current) || stageIndex(s) > stageIndex(target)) continue;
    const at = s === 'lead' ? null : (e[s as Exclude<SalesStage, 'lead'>] ?? null);
    // A stage passed through without its own evidence (a company created with no recorded
    // grant) is not given an event: inventing one would date a step nobody saw.
    if (at) out.push({stage: s, at});
  }
  // The target itself always has evidence, so it is always the last step.
  return out;
};

/**
 * VRG-701 — a company's evidence, as ONE of its deals may use it. The evidence is company-level
 * (a waitlist grant, a demo, a real platform company, the first paid invoice), and before several
 * deals per company it all belonged to the one deal. Now:
 *
 *   - the company's FIRST deal (earliest opened) takes all of it, exactly as before;
 *   - a LATER deal (an upsell, a pilot → rollout, a second attempt) takes only evidence dated
 *     AFTER it opened (less a minute for the sync's clock). Otherwise an expansion deal opened at
 *     a client would be moved straight to Won by the paid invoice that won the FIRST deal, and
 *     could never sit before Signed up.
 *
 * So a later deal moves by hand (a move past evidence asks to confirm, as it always has), and to
 * Won by VRG-700's automatic Won only for a subscription that started after it opened.
 */
export const LATER_DEAL_EVIDENCE_TOLERANCE_MS = 60_000;
export const evidenceForDeal = (e: StageEvidence, deal: {opened_at: string; is_first_deal: boolean}): StageEvidence => {
  if (deal.is_first_deal) return e;
  const floor = Date.parse(deal.opened_at) - LATER_DEAL_EVIDENCE_TOLERANCE_MS;
  const keep = (at: string | null | undefined): string | null => (at && Date.parse(at) >= floor ? at : null);
  return {access_granted: keep(e.access_granted), demo: keep(e.demo ?? null), account_created: keep(e.account_created), paid_client: keep(e.paid_client)};
};

/**
 * VRG-701 — which of a company's deals is its FIRST: earliest `opened_at`, ties by id. The one
 * rule the board, the sync and the automatic Won share.
 */
export const firstDealId = (deals: ReadonlyArray<{id: string; opened_at: string}>): string | null =>
  [...deals].sort((a, b) => a.opened_at.localeCompare(b.opened_at) || a.id.localeCompare(b.id))[0]?.id ?? null;

// ── The sales company's descriptive fields (VRG-696) ─────────────────────────────────────

// "Vera product" (VRG-696, `sales_accounts.vera_product`, 0152) became the deal's subscription
// PLAN (`DEAL_PLANS`, VRG-701, Spencer 2026-10-14); the column was dropped by 0159.

/**
 * VRG-696 — how a deal entered the pipeline, first touch. `waitlist`: a website signup was the
 * first thing Vera saw (the deal opened at it). `direct`: staff added it (a lead or company
 * created by hand, an event, a field visit), or a demo or platform company preceded any signup.
 */
export const PIPELINE_ENTRIES = ['waitlist', 'direct'] as const;
export type PipelineEntry = (typeof PIPELINE_ENTRIES)[number];
export const PIPELINE_ENTRY_LABELS: Readonly<Record<PipelineEntry, string>> = {
  waitlist: 'Joined waitlist',
  direct: 'Added directly',
};

// ── The deal as the board sees it, and the folds ─────────────────────────────────────────

export interface PipelineDeal {
  id: string;
  account_id: string;
  stage: SalesStage;
  status: DealStatus;
  closed_reason: DealClosedReason | null;
  /** A demo was ever provisioned for this company. */
  had_demo: boolean;
  value: DealValue;
  source_channel: SourceChannel | null;
  source_group: LeadSourceGroup;
  /** When the deal entered the pipeline (first touch), ISO. */
  opened_at: string;
  /** When each stage was first reached, ISO, from the stage history. */
  reached: Partial<Record<SalesStage, string>>;
  /**
   * VRG-692, aligned to PAYING by VRG-700 (Spencer, 2026-10-14) and to PAYING NOW by VRG-727 —
   * the linked company's paying-now subscriptions (`payingVerdict`: live mode, active, not
   * trialing, and a live paid invoice above $0 in the last 60 days), as ARR in USD, or null with
   * none. A test-mode subscription or a $0 pilot is NOT committed. A deal with one is COMMITTED:
   * it leaves Open pipeline and counts in Committed MRR/ARR. Equal to `paying_arr` by
   * construction. Optional: absent = none.
   */
  committed_arr?: number | null;
  /**
   * VRG-700 — Σ the ARR (USD) of the company's paying-now subscriptions attributed to this deal
   * (`payingVerdict`), or null with none. The master pipeline's value rule reads THIS: actual if
   * a paying subscription exists.
   */
  paying_arr?: number | null;
  /**
   * VRG-727 — the COMPANY's money status (`payingNowStatus`, gtm/paying.ts): Paying now (a
   * subscription is), Not paying (it was Won — a first paid invoice — and lapsed), or Never
   * paid. Company-level, so every deal at a company carries the same one. Absent = never paid.
   */
  paying_now?: PayingNowStatus;
  /** VRG-700 — when the earliest paying subscription started (ISO), or null. */
  paying_since?: string | null;
  /**
   * VRG-700 — the company has a live, active $0 subscription with no paid invoice (a pilot, or a
   * comped base fee with nothing billed). Shown as its own "Pilot ($0)" chip; never Committed.
   */
  pilot?: boolean;
  /**
   * VRG-700 — set when Vera moved the deal to Won itself (VRG-727: on a subscription's first paid
   * invoice; before, when it started paying), and it is still there: the subscription it moved for and when. Null once a person moves it
   * back (that records an override) or for a deal Won any other way.
   */
  auto_won?: {subscription_id: string; at: string} | null;
  /** VRG-692 — predicted vs actual ROI and ARR, with accuracy. */
  forecast?: DealForecast | null;
  /** VRG-701 — the deal's potential subscription + activation revenue, from its calculator inputs. */
  potential?: DealPotentialRevenue | null;
  /**
   * VRG-736 — tag margin a year (USD): expected tagged units × tag unit cost × markup. A SEPARATE
   * line, never in `value` (ARR). Null with no production figure. A number, so it can be summed
   * and grouped (ROI clustering).
   */
  tag_margin?: number | null;
  /** VRG-736 — predicted ARR (`value`, USD) + tag margin. Null without a USD ARR. */
  predicted_annual_value?: number | null;
  /** VRG-736 — how the predicted ARR was computed, for its tooltip (`predictedArrFormula`). */
  arr_breakdown?: PredictedArrBreakdown | null;
  /** VRG-696 — first-touch entry. Absent reads as `direct`. */
  entry?: PipelineEntry;
  /** VRG-696 — the sales company's descriptive fields, for slicing. Null = not set. */
  industry?: string | null;
  /**
   * VRG-701 — the plan the deal is on (from Stripe, when paying) or is buying (set on the deal).
   * Replaces VRG-696's company-level `vera_product`.
   */
  plan?: DealPlan | null;
  plan_source?: DealPlanSource | null;
  /** VRG-701 — an optional name, to tell two deals at one company apart ("Pilot", "Rollout"). */
  name?: string | null;
  /** VRG-701 — the company's earliest deal (its acquisition); later deals are upsells or retries. */
  is_first_deal?: boolean;
  /**
   * VRG-701 — the COMPANY's first-touch source, whatever the deal's own source says. Equal to
   * `source_channel` / `source_group` for a deal that inherits; the company-lifetime view
   * (`MasterAttribution` 'first_touch') reads these instead.
   */
  first_touch_channel?: SourceChannel | null;
  first_touch_group?: LeadSourceGroup;
  /**
   * VRG-701 — the sub-source the deal's source points at (its own when it has its own source,
   * else the company's): the grouping key (`kind:id`, `subSourceKey`) and its label. Null = none.
   * A free-text label with no reference (before 0156, or with no channel) keys as `text:<label>`.
   */
  sub_source?: {key: string; label: string} | null;
  /** VRG-701 — the company's client tier (`CLIENT_TIERS`, 'tier_1'…'tier_5'); null = not set. */
  tier?: string | null;
  /** VRG-701 — the company's sub-source, for the company-lifetime view. */
  first_touch_sub_source?: {key: string; label: string} | null;
  /** ISO 3166-1 alpha-2, upper case. */
  country?: string | null;
  city?: string | null;
}

/** Open and on an active subscription: counted in Committed, not in Open pipeline. */
export const isCommitted = (d: Pick<PipelineDeal, 'status' | 'committed_arr'>): boolean =>
  d.status === 'open' && d.committed_arr !== null && d.committed_arr !== undefined;

export interface DayRangeLike {
  /** Inclusive YYYY-MM-DD, UTC. Absent = unbounded ("All time"). */
  since?: string | null;
  until?: string | null;
}

/** Is an ISO instant inside an inclusive UTC day range? */
export const inDayRange = (iso: string | null | undefined, r: DayRangeLike | null | undefined): boolean => {
  if (!iso) return false;
  const day = iso.slice(0, 10);
  if (r?.since && day < r.since) return false;
  if (r?.until && day > r.until) return false;
  return true;
};

/** The lane a deal shows in: its stage, or Lost when closed as lost. (Archived: its stage, off the board.) */
export const boardLane = (d: Pick<PipelineDeal, 'stage' | 'status' | 'closed_reason'>): SalesBoardLane =>
  d.status === 'closed' && d.closed_reason === 'lost' ? 'lost' : d.stage;

/** USD amount or null — the only figures summed into the tiles and the value chart. */
export const usdValue = (v: DealValue): number | null =>
  v.amount !== null && v.currency === DEAL_VALUE_CURRENCY ? v.amount : null;

export interface PipelineKpis {
  open_deals: number;
  per_lane: Record<SalesBoardLane, number>;
  /** Open deals NOT on an active subscription: the ones Open pipeline sums. */
  open_pipeline_deals: number;
  /** Σ USD predicted value of open, uncommitted deals ("Open pipeline"). */
  open_value: number;
  /** Σ USD value × the stage's probability over the same deals ("Weighted pipeline"). */
  weighted_value: number;
  /** Open uncommitted deals with no value, or one in another currency (left out of both sums). */
  open_without_usd_value: number;
  /** VRG-736 — Σ tag margin a year over the same open, uncommitted deals. Beside Open pipeline, never in it. */
  open_tag_margin: number;
  /** VRG-692 — open deals on an active subscription, and what those subscriptions commit. */
  committed_deals: number;
  committed_arr: number;
  committed_mrr: number;
  /** VRG-692 — actual ÷ predicted, per company, over the companies with both. */
  accuracy: ForecastAccuracyKpis;
  won_count: number;
  won_value: number;
  /** Mean days from entering the pipeline to Won, deals that were won in the range. */
  avg_days_to_paid: number | null;
  /** Deals closed as lost (all time): the Lost column's count. */
  lost_deals: number;
}

const zeroLanes = (): Record<SalesBoardLane, number> =>
  Object.fromEntries(SALES_BOARD_LANES.map(l => [l, 0])) as Record<SalesBoardLane, number>;

const DAY_MS = 86_400_000;

export const foldPipelineKpis = (
  deals: readonly PipelineDeal[],
  probabilities: StageProbabilities,
  range: DayRangeLike | null,
): PipelineKpis => {
  const k: PipelineKpis = {
    open_deals: 0,
    per_lane: zeroLanes(),
    open_pipeline_deals: 0,
    open_value: 0,
    weighted_value: 0,
    open_without_usd_value: 0,
    open_tag_margin: 0,
    committed_deals: 0,
    committed_arr: 0,
    committed_mrr: 0,
    accuracy: foldForecastAccuracy(deals),
    lost_deals: 0,
    won_count: 0,
    won_value: 0,
    avg_days_to_paid: null,
  };
  let daysSum = 0;
  for (const d of deals) {
    if (d.status === 'open') {
      k.open_deals += 1;
      k.per_lane[boardLane(d)] += 1;
    } else if (d.closed_reason === 'lost') {
      k.lost_deals += 1;
      k.per_lane.lost += 1;
    }
    if (isCommitted(d)) {
      // Spencer (2026-10-13): a deal with an active subscription stops counting in Open
      // pipeline and counts in Committed — its money is now a contract, not a forecast.
      k.committed_deals += 1;
      k.committed_arr += d.committed_arr ?? 0;
    } else if (d.status === 'open') {
      k.open_pipeline_deals += 1;
      k.open_tag_margin += typeof d.tag_margin === 'number' && Number.isFinite(d.tag_margin) ? d.tag_margin : 0;
      const v = usdValue(d.value);
      if (v === null) k.open_without_usd_value += 1;
      else {
        k.open_value += v;
        k.weighted_value += v * (probabilities[d.stage] ?? DEFAULT_STAGE_PROBABILITIES[d.stage]);
      }
    }
    const paidAt = d.reached.paid_client;
    if (d.stage === 'paid_client' && paidAt && inDayRange(paidAt, range)) {
      k.won_count += 1;
      k.won_value += usdValue(d.value) ?? 0;
      daysSum += Math.max(0, (Date.parse(paidAt) - Date.parse(d.opened_at)) / DAY_MS);
    }
  }
  k.avg_days_to_paid = k.won_count > 0 ? daysSum / k.won_count : null;
  k.committed_mrr = k.committed_arr / 12;
  return k;
};

// The flow chart ---------------------------------------------------------------------------

export type PipelineFlowEnd = 'committed' | 'open' | 'lost' | 'archived';
export const PIPELINE_FLOW_END_LABELS: Readonly<Record<PipelineFlowEnd, string>> = {
  // VRG-692 — on an active subscription but not yet Won (no paid invoice seen).
  committed: 'Subscribed',
  open: 'Still open',
  lost: 'Lost',
  archived: 'Archived',
};

export type PipelineFlowNodeId = `source:${LeadSourceGroup}` | `stage:${SalesStage}` | `end:${PipelineFlowEnd}`;

export interface PipelineFlow {
  nodes: Array<{id: PipelineFlowNodeId; label: string; kind: 'source' | 'stage' | 'end'}>;
  links: Array<{source: PipelineFlowNodeId; target: PipelineFlowNodeId; count: number; value: number}>;
  /** Deals in the cohort (entered the pipeline in the range). */
  cohort: number;
  /** Cohort deals with no USD value: they flow in count mode, not in value mode. */
  without_usd_value: number;
}

/**
 * Every cohort deal walks: its source group → Lead → each later stage it REACHED (up to where
 * it is now) → an end: Won is its own end; otherwise Subscribed (open, on a subscription),
 * Still open, Lost or Archived. In = out at every stage node, by construction.
 */
/**
 * The stages a deal passed through, in order, up to where it is now: Lead, then each later
 * stage it REACHED (has a history event for), always including its current stage. A deal that
 * skipped a stage (a company created with no recorded grant) skips that node. The flow chart
 * and the by-channel table both walk this, which is what makes them agree.
 */
export const dealPath = (d: Pick<PipelineDeal, 'stage' | 'reached'>): SalesStage[] => {
  const path: SalesStage[] = ['lead'];
  for (const s of SALES_STAGES) {
    if (s === 'lead' || stageIndex(s) > stageIndex(d.stage)) continue;
    if (d.reached[s] || s === d.stage) path.push(s);
  }
  return path;
};

export const foldPipelineFlow = (deals: readonly PipelineDeal[], range: DayRangeLike | null): PipelineFlow => {
  const links = new Map<string, {source: PipelineFlowNodeId; target: PipelineFlowNodeId; count: number; value: number}>();
  const used = new Set<PipelineFlowNodeId>();
  const add = (source: PipelineFlowNodeId, target: PipelineFlowNodeId, value: number) => {
    const key = `${source}>${target}`;
    const l = links.get(key) ?? {source, target, count: 0, value: 0};
    l.count += 1;
    l.value += value;
    links.set(key, l);
    used.add(source);
    used.add(target);
  };
  let cohort = 0;
  let withoutValue = 0;
  for (const d of deals) {
    if (!inDayRange(d.opened_at, range)) continue;
    cohort += 1;
    const v = usdValue(d.value);
    if (v === null) withoutValue += 1;
    const value = v ?? 0;
    const path = dealPath(d);
    add(`source:${d.source_group}`, 'stage:lead', value);
    for (let i = 1; i < path.length; i += 1) add(`stage:${path[i - 1]}`, `stage:${path[i]}`, value);
    const last = path[path.length - 1];
    if (last !== 'paid_client') {
      const end: PipelineFlowEnd =
        d.status === 'closed'
          ? d.closed_reason === 'archived'
            ? 'archived'
            : 'lost'
          : isCommitted(d)
            ? 'committed'
            : 'open';
      add(`stage:${last}`, `end:${end}`, value);
    }
  }
  const nodes: PipelineFlow['nodes'] = [];
  for (const g of LEAD_SOURCE_GROUPS) if (used.has(`source:${g}`)) nodes.push({id: `source:${g}`, label: LEAD_SOURCE_GROUP_LABELS[g], kind: 'source'});
  for (const s of SALES_STAGES) if (used.has(`stage:${s}`)) nodes.push({id: `stage:${s}`, label: SALES_STAGE_LABELS[s], kind: 'stage'});
  for (const e of ['committed', 'open', 'lost', 'archived'] as const) {
    if (used.has(`end:${e}`)) nodes.push({id: `end:${e}`, label: PIPELINE_FLOW_END_LABELS[e], kind: 'end'});
  }
  return {nodes, links: [...links.values()], cohort, without_usd_value: withoutValue};
};

// The by-channel table ---------------------------------------------------------------------

export interface PipelineChannelCounts {
  /** `null` = not a channel: Expansion when `expansion`, else not recorded. */
  channel: SourceChannel | null;
  /** VRG-701 — the row is the Expansion source (an existing client's later deal), not a channel. */
  expansion?: boolean;
  /** Entered the pipeline in the range (every deal starts as a Lead). */
  lead: number;
  access_granted: number;
  /** Passed through the Demo stage (VRG-692: a step on the path, no longer a side count). */
  demo: number;
  account_created: number;
  paid_client: number;
  /** VRG-692 — of the cohort, open deals on an active subscription now. */
  committed: number;
}

/**
 * The VRG-675 by-channel table, now counted from the deals: for the cohort that entered in the
 * range, how many passed through each stage (`dealPath`). Same cohort and same walk as the flow
 * chart, so a column total equals the chart's flow into that stage node.
 */
export const foldPipelineByChannel = (deals: readonly PipelineDeal[], range: DayRangeLike | null): PipelineChannelCounts[] => {
  const by = new Map<string, PipelineChannelCounts>();
  for (const d of deals) {
    if (!inDayRange(d.opened_at, range)) continue;
    // VRG-701 — Expansion is a row of its own, never merged into Not recorded.
    const expansion = !d.source_channel && d.source_group === 'expansion';
    const key = d.source_channel ?? (expansion ? '(expansion)' : '(none)');
    const row = by.get(key) ?? {channel: d.source_channel, expansion, lead: 0, access_granted: 0, demo: 0, account_created: 0, paid_client: 0, committed: 0};
    for (const s of dealPath(d)) row[s] += 1;
    if (isCommitted(d)) row.committed += 1;
    by.set(key, row);
  }
  return [...by.values()].sort((a, b) => {
    if (a.channel === null && b.channel === null) return a.expansion ? -1 : b.expansion ? 1 : 0;
    if (a.channel === null) return 1;
    if (b.channel === null) return -1;
    return b.lead - a.lead || b.paid_client - a.paid_client;
  });
};
